/**
 * Brand-styled icons. Visual language matches /public/favicon.svg:
 * layered rounded rectangles in the Develio palette — teal, green,
 * orange triangle accent, navy foreground. Used on dashboard tiles so
 * the admin quick-links feel like part of the brand, not stock emoji.
 */

interface IconProps {
  /** Pixel size (width + height). Defaults to 40. */
  size?: number;
  className?: string;
}

/**
 * Jurisdiction profile — abstract skyline: three rounded rectangles of
 * ascending height in teal / green / navy, with an orange triangle accent
 * suggesting a municipal banner.
 */
export function JurisdictionIcon({ size = 40, className = '' }: IconProps) {
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      {/* Back: teal tall tower */}
      <rect x="10" y="20" width="28" height="70" rx="6" fill="#1E9595" />
      {/* Mid-back: green mid tower */}
      <rect x="34" y="34" width="32" height="56" rx="6" fill="#52C030" />
      {/* Orange triangle flag accent on the green tower */}
      <polygon points="66,34 92,34 66,54" fill="#E05018" />
      {/* Front: navy short tower */}
      <rect x="60" y="48" width="30" height="42" rx="6" fill="#2D4B5E" />
    </svg>
  );
}

/**
 * RIMS multipliers — abstract chart: three ascending rounded bars in
 * teal / green / navy, with a small orange triangle accent on the tallest
 * bar echoing the logo's triangle motif.
 */
export function RimsIcon({ size = 40, className = '' }: IconProps) {
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      {/* Shortest bar: navy */}
      <rect x="10" y="58" width="22" height="32" rx="5" fill="#2D4B5E" />
      {/* Middle bar: teal */}
      <rect x="38" y="38" width="22" height="52" rx="5" fill="#1E9595" />
      {/* Tallest bar: green */}
      <rect x="66" y="18" width="22" height="72" rx="5" fill="#52C030" />
      {/* Orange triangle accent on the tallest bar — growth marker */}
      <polygon points="66,18 88,18 66,36" fill="#E05018" />
    </svg>
  );
}

/**
 * New analysis — a rounded "+" mark styled as a logo block, not a text
 * character. Teal back rect, green front rect, white plus. Included here
 * so the three dashboard quick-links share one visual family.
 */
export function NewAnalysisIcon({ size = 40, className = '' }: IconProps) {
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      {/* Back: teal */}
      <rect x="14" y="14" width="68" height="68" rx="14" fill="#1E9595" />
      {/* Front: green, inset */}
      <rect x="22" y="22" width="60" height="60" rx="11" fill="#52C030" />
      {/* Plus */}
      <rect x="46" y="30" width="8" height="40" rx="3" fill="#FFFFFF" />
      <rect x="30" y="46" width="40" height="8" rx="3" fill="#FFFFFF" />
    </svg>
  );
}
